import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getTheme } from './theme';
import { useThemeStore } from './store/useThemeStore';
import i18n from './i18n/config';
import { msalInstance, initializeMsal } from './config/msal';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastContainer } from './components/common/Toast';
import { AuthGuard } from './components/auth/AuthGuard';
import { RoleGuard } from './components/auth/RoleGuard';
import { MainLayout } from './layouts/MainLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { ROUTES } from './utils/constants';
import { UserRole } from './types';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Upload = lazy(() => import('./pages/Upload').then(m => ({ default: m.Upload })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Results = lazy(() => import('./pages/Results').then(m => ({ default: m.Results })));
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading fallback component
function LoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function App() {
  const { mode } = useThemeStore();
  const theme = getTheme(mode);

  // Initialize MSAL on app start
  useEffect(() => {
    initializeMsal().catch((error) => {
      console.error('Failed to initialize MSAL:', error);
    });
  }, []);

  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <BrowserRouter>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Auth routes - Public */}
                    <Route element={<AuthLayout />}>
                      <Route path={ROUTES.LOGIN} element={<Login />} />
                    </Route>

                    {/* Main app routes - Protected */}
                    <Route
                      element={
                        <AuthGuard>
                          <MainLayout />
                        </AuthGuard>
                      }
                    >
                      <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                      <Route path={ROUTES.UPLOAD} element={<Upload />} />
                      <Route path={ROUTES.HISTORY} element={<History />} />
                      <Route path={ROUTES.RESULTS} element={<Results />} />
                      <Route path={ROUTES.PROFILE} element={<Profile />} />

                      {/* Admin route - Role protected */}
                      <Route
                        path={ROUTES.ADMIN}
                        element={
                          <RoleGuard
                            roles={[UserRole.SystemAdmin, UserRole.AuthorityAdmin]}
                          >
                            <Admin />
                          </RoleGuard>
                        }
                      />
                    </Route>

                    {/* Root redirect */}
                    <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

                    {/* 404 - Not Found */}
                    <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>

              {/* Toast notifications */}
              <ToastContainer />
            </ThemeProvider>
          </I18nextProvider>

          {/* React Query Devtools - Only in development */}
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}

export default App;
